/**
 * Define "column definition" objects that tell tables how to render a column.
 */
import produce from 'immer';
import React from 'react';

import {assert} from 'Asserts';
import {BioentityType} from 'constants/ModelTypeConstants';
import {Side} from 'constants/MolbioConstants';
import {SchemaSummary} from 'constants/RegistryConstants';
import {SchemaFieldDataType} from 'folder-items/FileConstants';
import {AntibodyDomainType, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EntitySelectionMode} from 'inventory/InventoryConstants';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {LookupSortType} from 'lookup-config/LookupConfigModalConstants';
import {
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  SpecifiedFragmentInfo,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {OptionCellDropdownOption} from 'notebook/plain-tables/selectors/OptionCellDropdown';
import {
  BASES_COLUMN_NAME,
  BASES_HELM_COLUMN_NAME,
  BASES_IDT_COLUMN_NAME,
} from 'notebook/registration-tables/RegistrationTableUtil';
import {
  BasesColumnConfig,
  getBasesColumnConfigDisplayName,
} from 'notebook/structured-tables/bases-columns/BasesColumnConstants';
import {CustomColumnConfig} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {
  LookupConfig,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  UnitReferenceColumnDisplayMode,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {Cell, TableColumnType, TypedValue} from 'notebook/tables/TableConstants';
import {
  ContainersColumnMode,
  TransferTableColumnGroup,
} from 'notebook/transfer-tables/TransferTableConstants';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {UIRequestTask} from 'requests/tasks/RequestTaskConstants';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {FrozenEntityChipData} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  PlateMaterialInput,
  ReagentAssignmentGroup,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {ParentStorableType, PlateSchemaType, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FormulaIcon from 'ui-library/icons/FormulaIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {StageRun} from 'workflows/WorkflowConstants';

import {ProcessTaskStatusLifecycleGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessTaskStatusLifecycleGraphQL';
import {RequestTaskGraphQL} from '../../../../graphql-fragments/requests/__generated/RequestTaskGraphQL';
import {UnitTypePredicates} from './UnitTypePredicates';

// Column groups are a way to distinguish between column definitions
// with similar type in a single table.  For instance, we may be
// transferring between two containers with the same schema.  One of
// the schemas will be labeled with a columnGroup of SOURCE while the
// other will be a columnGroup of TARGET.  StructuredTables can also
// have columnGroups.
export type ColumnGroup = TransferTableColumnGroup | StructuredTableColumnGroups | null;

interface BaseColumnDefinition {
  type: TableColumnType;
  columnGroup?: ColumnGroup | null;
  isRequired: boolean;
  name?: string;

  // Should be true if the column is a read-only column
  locked?: boolean;
}

export type WithMaybeLinkedSampleColumnDefinition =
  | ContainersColumnDefinition
  | ParentStorableColumnDefinition
  | PositionColumnDefinition;

export interface StringColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.STRING;
  name: string;
}

export interface BooleanColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BOOLEAN;
  name: string;
}

export interface DNABasesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.DNA_BASES;
}

export interface RNABasesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.RNA_BASES;
}
export interface DNAHELMColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.DNA_HELM;
}
export interface RNAHELMColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.RNA_HELM;
}
export interface DNAIDTColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.DNA_IDT;
}
export interface RNAIDTColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.RNA_IDT;
}

export interface BasesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BASES;
  basesColumnConfig: BasesColumnConfig;
}

export interface SequenceTopologySelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.SEQUENCE_TOPOLOGY;
}

export interface SmilesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.SMILES;
}

export interface BatchColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BATCH;
}

export interface BioentityNameColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BIOENTITY_NAME;
}

export interface RequestTaskSelectionColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.REQUEST_TASK_SELECTION;
  requestTask: RequestTaskGraphQL | UIRequestTask;
}

export interface AliasColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.ALIAS;
}

export interface ConcentrationColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.CONCENTRATION;
  units: string;
  unitApiIdentifier?: string;
  hideContextMenu: boolean;
}

export interface ContainersColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.CONTAINERS;
  linkedSampleSchemaField?: SchemaFieldConstants.SchemaField;
  containersColumnMode: ContainersColumnMode;
  containerSchemaId?: string;
  containerSchemaName?: string;
}

export interface DedicatedLookupColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.DEDICATED_LOOKUP;
  name: string;
  inputColumnProvenanceUuid: string;
  lookupConfig: LookupConfig;
  lookupSortType?: LookupSortType;
}

export interface CustomColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.CUSTOM;
  name: string;
  customColumnConfig: CustomColumnConfig;
}

export interface ExistingBioentityColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.EXISTING_BIOENTITY;
  // Filters the bioentity to one particular tag schema or interface. 'ALL' indicates we don't want to filter
  // to a specific schema.
  tagSchemaOrInterface: SchemaSummary | 'ALL';
  bioentityTypeFilters?: Array<BioentityType>;
}

export interface BioentitySelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BIOENTITY_SELECTOR;
  schemaFieldForFiltering: SchemaFieldDataType | null;
}

export interface FulfillableStatusColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.FULFILLABLE_STATUS;
  requestTask: RequestTaskGraphQL | UIRequestTask;
}

export interface NumberOfContainersColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.NUMBER_OF_CONTAINERS;
}

export interface ParentStorableColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PARENT_STORABLE;
  isForTransfer: boolean;
  // Schema of storables that will go in the parent storable.
  // Is used for string resolution and is not required for rendering
  storableSchema?: StorableSchema | null;
  // Whether the parent storable is a Grid or a Location.
  // Is used for string resolution and cell editing and is not required for rendering
  // DEPRECATED - prefer parentStorableType for new structured tables.
  parentStorableIsGrid?: boolean;
  // Parent storable type can be: GRID, LOCATION, or both (GRID_OR_LOCATION)
  // Is used for string resolution and cell editing and is not required for rendering
  parentStorableType: ParentStorableType;
  // Used to enable location searching when schema is null
  fallbackRegistryId?: string | null;
  linkedSampleSchemaField?: SchemaFieldConstants.SchemaField;
}

export interface PlateColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PLATE;
  registryId: string | null;
  plateSchemaId?: string;
  plateSchemaName?: string;
  plateSchemaType?: PlateSchemaType;
  containerSchema?: {id: string; name: string};
}

export interface BoxColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BOX;
  registryId: string | null;
  boxSchemaId?: string;
  boxSchemaName?: string;
  containerSchema?: {id: string; name: string};
}

export interface PositionColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.POSITION;
  linkedSampleSchemaField?: SchemaFieldConstants.SchemaField;
}

export interface PartiesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PARTIES;
}

export interface StudiesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.STUDIES;
}

export interface SampleColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.SAMPLE;
}

export interface SampleGroupSampleColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.SAMPLE_GROUP_SAMPLE;
  tagSchemaFieldType: SchemaFieldConstants.FieldType;
  tagSchemaFieldApiId: string;
  tagSchemaFieldDisplayName: string;
  tagSchemaFieldIsComputed: boolean;
  tagSchemaFieldIsConvertedFromLink: boolean;
  // The schemaField prop contains live unversioned value of tag schema field. It should be only used for
  // editing and server actions, not for rendering
  schemaField: SchemaFieldConstants.SchemaField;
}

export interface SampleRestrictionStatusColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.SAMPLE_RESTRICTION_STATUS;
}

export interface UnitSelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.UNIT_SELECTOR;
  // The list of valid units is not persisted. It is a run-time concern only, set by the adapter
  acceptedUnits: Array<string>;
}

export interface OptionSelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.OPTION_SELECTOR;
  /** The list of valid text options */
  options: Array<string | OptionCellDropdownOption>;
}

export interface OptionSearchSelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.OPTION_SEARCH_SELECTOR;
  /** The list of valid text options */
  options: Array<string | OptionData>;
  emptyOptionsDisplay?: React.ReactNode;
  isMulti?: boolean;
}
export interface PlateLayoutColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PLATE_LAYOUT_SELECTOR;
  runSchema: Schema | null;
}

export interface LookupConfigColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.LOOKUP_CONFIG;
}

export interface ProcessTaskColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCESS_TASK;
  // Front-end metadata: the associated process schema. (Any of: processSchema, processTaskSchema, processOutputSchema)
  // The schema is used during string resolution, to filter the search for tasks. While the only schema that is needed
  // for string resolution is the task schema, any of the schema types can (and will) be converted into the task schema
  // on the back-end. This frees the FE from having to run an extra query to do this conversion.
  schemaId: string | null;
}

export interface ProcessTaskAssigneeColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCESS_TASK_ASSIGNEE;
}

export interface ProcessTaskStatusColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCESS_TASK_STATUS;
  // The statusLifecycle is not persisted here. It is a run-time concern only, set by the adapter
  // Since the statusLifecycle lives on the process task schema, it is possible for the statusLifecycle
  // to be undefined when the schema is inaccessible.
  statusLifecycle?: ProcessTaskStatusLifecycleGraphQL;
}

export interface ProcessTaskScheduledOnDateColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE;
}

export interface ProcessTaskExecutionOriginColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN;
}

export interface ProcessOutputColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCESS_OUTPUT;
}

export interface ProcedureColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCEDURE;
  procedureTypes?: Array<ProcedureType>;
  placeholder?: string;
  shouldAllowMultiple?: boolean;
}

export interface ProcedureSchemaFieldTypeColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE;
  context: ProcedureSchemaFieldTypeContext;
}

export interface ProcedureParameterConfirmedInColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN;
  navigateToStep: (stepCid: string) => void;
}

export interface ProcedureParameterMinMaxColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCEDURE_PARAMETER_MIN_MAX;
}

export interface ProcedureConditionReplicatesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCEDURE_CONDITION_REPLICATES;
}

export interface ProcedureLibraryParameterSelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR;
}

export interface TestDefinitionColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.TEST_DEFINITION;
  /** If specified, the organization that the test definition must belong to. Used for filtering. */
  targetOrganization?: {id: string; name: string};
}

export interface MeasurementAmountColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.MEASUREMENT_AMOUNT;
  name: string;
  isComputed?: boolean;
  allowQS: boolean;
  allowNegative?: boolean;
  allowZero?: boolean;
}

export interface IngredientCatalogIdColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.INGREDIENT_CATALOG_ID;
}

export interface IngredientComponentLotTextColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT;
}

export interface IngredientNotesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.INGREDIENT_NOTES;
}

export interface BulkAssemblyFragmentSequenceColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER;
  bioentityTypeFilters: Array<BioentityType>;
}

export interface BulkAssemblyPreferredPrimerColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER;
}

export interface BulkAssemblyBinSelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR;
  bins: Array<BulkAssemblyFragmentBin>;
}

export interface BulkAssemblyFragmentEndpointColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT;
  side: Side;
}

export interface BulkAssemblyLengthColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_LENGTH;
}

export interface BulkAssemblyConstructNameColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME;
}

export interface BulkAssemblyValidationStatusColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS;
}

export interface BulkAssemblyPrimerColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_PRIMER;
}

export interface BulkAssemblyPrimerBasesColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_PRIMER_BASES;
}

export interface BulkAssemblyFragmentSelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR;
  fragmentDatas: Array<SpecifiedFragmentInfo>;
  binColor: string;
  method: BulkAssemblyMethod;
  assemblyId: string;
}

export interface BulkAssemblyRestrictionEnzymeSelectorColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME;
}

export interface AnonymousTagValueColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.ANONYMOUS_TAG_VALUE;
}

export interface ExperimentalRoleColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.EXPERIMENTAL_ROLE;
}

export interface FillPlateMeasurementAmountColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT;
}

export interface FillPlateSourceChipColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP;
  frozenEntityChipData?: {[id: string]: FrozenEntityChipData};
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  testOrderInfoById: TestOrderInfoById;
}

export interface FillPlateSourceContainerChipColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP;
  frozenEntityChipData: {[id: string]: FrozenEntityChipData};
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  testOrderInfoById: TestOrderInfoById;
}

export interface FillPlateSourceIconColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON;
}

export interface FillPlateSourceSchemaChipColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP;
}

export interface PlateMapMaterialInputColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.PLATE_MAP_MATERIAL_INPUT;
  plateMaterialInputs: Array<PlateMaterialInput>;
}

export interface UnitReferenceColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.UNIT_REFERENCE;
  unitTypesFilterPredicateType?: UnitTypePredicates;
  /** Filters unit options to a single unit type by ID */
  unitTypeId?: string;
  /* A value for which the column will not try to calculate a unit and will also not raise an error. */
  ignoreErrorWhenTypedValue?: TypedValue;
  /** Defaults to CHIP */
  displayMode?: UnitReferenceColumnDisplayMode;
}

export interface ComplexPolymerComponentColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.COMPLEX_POLYMER_COMPONENT;
  componentSchemaField: SchemaFieldConstants.BioentityLinkSchemaField | null;
  entitySelectionMode: EntitySelectionMode;
}

export interface ComplexPolymerConnectionColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.COMPLEX_POLYMER_CONNECTIONS;
}

export interface OligoDuplexOffsetColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.OLIGO_DUPLEX_OFFSET;
}

export interface MissingFieldColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.MISSING_FIELD;
  structuredTableColumnName: string;
  isArchived: boolean;
}

export interface ArchivedFieldColumnDefinition extends MissingFieldColumnDefinition {
  tagSchemaFieldType: SchemaFieldConstants.FieldType;
  tagSchemaFieldApiId: string;
  tagSchemaFieldDisplayName: string;
  tagSchemaFieldIsComputed: boolean;
  tagSchemaFieldIsConvertedFromLink: boolean;
  tagSchemaFieldIsSnapshot: boolean;

  // The schemaField prop contains live unversioned value of tag schema field. It should be only used for
  // editing and server actions, not for rendering
  schemaField: SchemaFieldConstants.SchemaField;
}

export interface SchemaFieldColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.TAG_VALUE;
  tagSchemaFieldType: SchemaFieldConstants.FieldType;
  tagSchemaFieldApiId: string;
  tagSchemaFieldDisplayName: string;
  tagSchemaFieldDescription?: string | null;
  tagSchemaFieldDecimalPrecision?: number | null;
  tagSchemaFieldIsComputed: boolean;
  tagSchemaFieldIsConvertedFromLink: boolean;
  tagSchemaFieldIsSnapshot: boolean;
  skipSchemaValidation?: boolean;
  unitApiIdentifier: string | null;

  // The props below contain live unversioned values of tag schema field. They should be only used for editing
  // and server actions, not for rendering
  schemaField: SchemaFieldConstants.SchemaField;
  selector?: SchemaFieldSelector | null;
}

type WithSchemaFieldColumnDefinition =
  | SchemaFieldColumnDefinition
  | ArchivedFieldColumnDefinition
  | SampleGroupSampleColumnDefinition;

export function isWithSchemaFieldColumnDefinition(
  columnDefinition: ColumnDefinition
): columnDefinition is WithSchemaFieldColumnDefinition {
  return (columnDefinition as WithSchemaFieldColumnDefinition).schemaField !== undefined;
}

export function isArchivedFieldColumnDefinition(
  columnDefinition: ColumnDefinition
): columnDefinition is ArchivedFieldColumnDefinition {
  return (
    columnDefinition.type === TableColumnType.MISSING_FIELD &&
    isWithSchemaFieldColumnDefinition(columnDefinition)
  );
}

export interface StageRunColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.STAGE_RUN;
  stageRuns: Array<StageRun>;
}

export interface QuantityColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.QUANTITY;
  units: string;
  unitApiIdentifier?: string;
  hideContextMenu: boolean;
}

export type UnitsColumnDefinition = QuantityColumnDefinition | ConcentrationColumnDefinition;

export interface RowDelegateColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.ROW_DELEGATE;
}

export interface AntibodyDomainColumnDefinition extends BaseColumnDefinition {
  type: TableColumnType.ANTIBODY_DOMAIN;
  domainType: AntibodyDomainType;
  specificity: string | null;
}

export type ColumnDefinition =
  | StringColumnDefinition
  | AliasColumnDefinition
  | BatchColumnDefinition
  | BioentityNameColumnDefinition
  | BooleanColumnDefinition
  | BoxColumnDefinition
  | ConcentrationColumnDefinition
  | ContainersColumnDefinition
  | CustomColumnDefinition
  | DedicatedLookupColumnDefinition
  | DNABasesColumnDefinition
  | RNABasesColumnDefinition
  | DNAHELMColumnDefinition
  | RNAHELMColumnDefinition
  | DNAIDTColumnDefinition
  | RNAIDTColumnDefinition
  | BasesColumnDefinition
  | SequenceTopologySelectorColumnDefinition
  | SmilesColumnDefinition
  | FulfillableStatusColumnDefinition
  | ExistingBioentityColumnDefinition
  | BioentitySelectorColumnDefinition
  | IngredientCatalogIdColumnDefinition
  | IngredientComponentLotTextColumnDefinition
  | IngredientNotesColumnDefinition
  | BulkAssemblyFragmentSequenceColumnDefinition
  | BulkAssemblyPreferredPrimerColumnDefinition
  | BulkAssemblyBinSelectorColumnDefinition
  | BulkAssemblyFragmentEndpointColumnDefinition
  | BulkAssemblyLengthColumnDefinition
  | BulkAssemblyConstructNameColumnDefinition
  | BulkAssemblyValidationStatusColumnDefinition
  | BulkAssemblyPrimerColumnDefinition
  | BulkAssemblyPrimerBasesColumnDefinition
  | BulkAssemblyFragmentSelectorColumnDefinition
  | BulkAssemblyRestrictionEnzymeSelectorColumnDefinition
  | AnonymousTagValueColumnDefinition
  | ExperimentalRoleColumnDefinition
  | FillPlateSourceChipColumnDefinition
  | FillPlateSourceContainerChipColumnDefinition
  | FillPlateSourceIconColumnDefinition
  | FillPlateSourceSchemaChipColumnDefinition
  | FillPlateMeasurementAmountColumnDefinition
  | PlateMapMaterialInputColumnDefinition
  | MissingFieldColumnDefinition
  | NumberOfContainersColumnDefinition
  | OptionSelectorColumnDefinition
  | OptionSearchSelectorColumnDefinition
  | ProcedureColumnDefinition
  | ProcedureLibraryParameterSelectorColumnDefinition
  | ProcedureParameterConfirmedInColumnDefinition
  | ProcedureParameterMinMaxColumnDefinition
  | ProcedureConditionReplicatesColumnDefinition
  | ParentStorableColumnDefinition
  | PartiesColumnDefinition
  | PlateColumnDefinition
  | PositionColumnDefinition
  | RequestTaskSelectionColumnDefinition
  | SampleColumnDefinition
  | SampleGroupSampleColumnDefinition
  | SampleRestrictionStatusColumnDefinition
  | SchemaFieldColumnDefinition
  | StageRunColumnDefinition
  | UnitSelectorColumnDefinition
  | QuantityColumnDefinition
  | MeasurementAmountColumnDefinition
  | LookupConfigColumnDefinition
  | PlateLayoutColumnDefinition
  | ProcessTaskColumnDefinition
  | ProcessTaskAssigneeColumnDefinition
  | ProcessTaskStatusColumnDefinition
  | ProcessTaskExecutionOriginColumnDefinition
  | ProcessTaskScheduledOnDateColumnDefinition
  | ProcessOutputColumnDefinition
  | ProcedureSchemaFieldTypeColumnDefinition
  | TestDefinitionColumnDefinition
  | StudiesColumnDefinition
  | RowDelegateColumnDefinition
  | UnitReferenceColumnDefinition
  | ComplexPolymerComponentColumnDefinition
  | ComplexPolymerConnectionColumnDefinition
  | OligoDuplexOffsetColumnDefinition
  | AntibodyDomainColumnDefinition;

export const INGREDIENT_CATALOG_IDENTIFIER_COLUMN_NAME = 'Catalog #';
export const INGREDIENT_COMPONENT_LOT_TEXT_COLUMN_NAME = 'Component Lot Text';
export const INGREDIENT_COMPONENT_LOT_CONTAINER_COLUMN_NAME = 'Component Lot Container';
export const INGREDIENT_COMPONENT_LOT_ENTITY_COLUMN_NAME = 'Component Lot Entity';
export const INGREDIENT_NOTES_COLUMN_NAME = 'Notes';
export const UNITS_COLUMN_NAME = 'Units';
// These are column names shared by any structured table that supports registering bioentities
export const BIOENTITY_NAME_COLUMN_NAME = 'Entity Name';
export const ALIAS_COLUMN_NAME = 'Alias';
export const EXISTING_BIOENTITY_COLUMN_NAME = 'Entity';

/**
 * Return the string for the name at the top of the column.
 *
 * Keep in sync with get_column_header_label() in benchling/notebook/structured_tables/columns/column_labels.py
 */
export function getColumnLabel(columnDefinition: ColumnDefinition): string {
  switch (columnDefinition.type) {
    case TableColumnType.ALIAS:
      return ALIAS_COLUMN_NAME;
    case TableColumnType.BATCH:
      return 'Batch';
    case TableColumnType.BOX:
      return columnDefinition.name || 'Box';
    case TableColumnType.DNA_BASES:
    case TableColumnType.RNA_BASES:
      return BASES_COLUMN_NAME;
    case TableColumnType.DNA_HELM:
    case TableColumnType.RNA_HELM:
      return BASES_HELM_COLUMN_NAME;
    case TableColumnType.DNA_IDT:
    case TableColumnType.RNA_IDT:
      return BASES_IDT_COLUMN_NAME;
    case TableColumnType.BASES:
      return getBasesColumnConfigDisplayName(columnDefinition.basesColumnConfig);
    case TableColumnType.SEQUENCE_TOPOLOGY:
      return 'Topology';
    case TableColumnType.SMILES:
      return 'SMILES';
    case TableColumnType.CONCENTRATION:
      return `Concentration (${columnDefinition.units})`;
    case TableColumnType.CONTAINERS:
      if (columnDefinition.columnGroup === TransferTableColumnGroup.SOURCE) {
        // TODO(BNCH-14682): can remove this special case once we backfill column names
        return 'Source Container';
      } else {
        return columnDefinition.name || 'Containers';
      }
    case TableColumnType.DEDICATED_LOOKUP:
      return columnDefinition.name;
    case TableColumnType.CUSTOM:
      return columnDefinition.name;
    case TableColumnType.BOOLEAN:
      return columnDefinition.name;
    case TableColumnType.BIOENTITY_NAME:
      return BIOENTITY_NAME_COLUMN_NAME;
    case TableColumnType.EXISTING_BIOENTITY:
      return columnDefinition.columnGroup === TransferTableColumnGroup.SOURCE
        ? 'Source Entity'
        : columnDefinition.name || EXISTING_BIOENTITY_COLUMN_NAME;
    case TableColumnType.BIOENTITY_SELECTOR:
      return EXISTING_BIOENTITY_COLUMN_NAME;
    case TableColumnType.MEASUREMENT_AMOUNT:
      return columnDefinition.name;
    case TableColumnType.MISSING_FIELD:
      return columnDefinition.isArchived
        ? `Archived: ${columnDefinition.structuredTableColumnName}`
        : `Missing: ${columnDefinition.structuredTableColumnName}`;
    case TableColumnType.NUMBER_OF_CONTAINERS:
      return 'Number of containers';
    case TableColumnType.PARENT_STORABLE:
      return columnDefinition.columnGroup != null && columnDefinition.name != null
        ? columnDefinition.name
        : 'Location';
    case TableColumnType.PARTIES:
      return columnDefinition.columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS
        ? 'Sample owners'
        : columnDefinition.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS
          ? getRestrictedSampleUsersHeaderText()
          : 'Parties';
    case TableColumnType.PLATE:
      return columnDefinition.name || 'Plate';
    case TableColumnType.POSITION:
      // New transfer table
      // TODO(BNCH-14682): can remove this hacky `name` check once we backfill column names
      // Use provided 'Source' or 'Destination' name (new transfer tables)
      // Use 'Source Position' name by default when column is in SOURCE column group (old transfer tables)
      // and 'Position' otherwise (inventory tables)
      if (
        columnDefinition.name === 'Source Well' ||
        (columnDefinition.name && columnDefinition.name.includes('Destination'))
      ) {
        return columnDefinition.name;
      }

      // Old transfer table
      if (columnDefinition.columnGroup === TransferTableColumnGroup.SOURCE) {
        return 'Source Position';
      } else {
        // Inventory table
        return 'Position';
      }
    case TableColumnType.SAMPLE:
      return 'Sample';
    case TableColumnType.SAMPLE_RESTRICTION_STATUS:
      return 'Restriction status';
    case TableColumnType.SAMPLE_GROUP_SAMPLE:
    case TableColumnType.TAG_VALUE:
      return columnDefinition.tagSchemaFieldDisplayName;
    case TableColumnType.STAGE_RUN:
      return 'Stage Run';
    case TableColumnType.PROCESS_TASK:
      return 'Task';
    case TableColumnType.PROCESS_TASK_ASSIGNEE:
      return 'Assignee';
    case TableColumnType.PROCESS_TASK_STATUS:
      return 'Status';
    case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE:
      return 'Scheduled on';
    case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
      return 'Execution Entry';
    case TableColumnType.PROCESS_OUTPUT:
      return 'Output';
    case TableColumnType.PROCEDURE:
      return columnDefinition.name ?? '';
    case TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR:
      return columnDefinition.name ?? 'Name';
    case TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE:
      return columnDefinition.name ?? 'Data type';
    case TableColumnType.PROCEDURE_CONDITION_REPLICATES:
      return columnDefinition.name ?? 'Condition - Replicate';
    case TableColumnType.TEST_DEFINITION:
      return columnDefinition.name ?? 'Test definition';
    case TableColumnType.UNIT_SELECTOR:
      return columnDefinition.name ?? UNITS_COLUMN_NAME;
    case TableColumnType.QUANTITY:
      return `${getQuantityDisplayString(true)} (${columnDefinition.units})`;
    case TableColumnType.STRING:
      return columnDefinition.name;
    case TableColumnType.INGREDIENT_CATALOG_ID:
      return INGREDIENT_CATALOG_IDENTIFIER_COLUMN_NAME;
    case TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT:
      return INGREDIENT_COMPONENT_LOT_TEXT_COLUMN_NAME;
    case TableColumnType.INGREDIENT_NOTES:
      return INGREDIENT_NOTES_COLUMN_NAME;
    case TableColumnType.ANONYMOUS_TAG_VALUE:
    case TableColumnType.EXPERIMENTAL_ROLE:
    case TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP:
    case TableColumnType.PLATE_MAP_MATERIAL_INPUT:
      return columnDefinition.name ?? '';
    case TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER:
    case TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER:
    case TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR:
    case TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT:
    case TableColumnType.BULK_ASSEMBLY_LENGTH:
    case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME:
    case TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS:
    case TableColumnType.BULK_ASSEMBLY_PRIMER:
    case TableColumnType.BULK_ASSEMBLY_PRIMER_BASES:
    case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR:
    case TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME:
      // Column headings are always specified explicitly by bulk assembly tables.
      return '';
    case TableColumnType.STUDIES:
      return 'Studies';
    case TableColumnType.OPTION_SELECTOR:
      // Option selector should always have an explicit column name.
      return '';
    case TableColumnType.OPTION_SEARCH_SELECTOR:
      // Option seaarch selector should always have an explicit column name.
      return '';
    case TableColumnType.ROW_DELEGATE:
      return columnDefinition.name ?? 'Value';
    case TableColumnType.UNIT_REFERENCE:
      return columnDefinition.name ?? 'Unit';
    case TableColumnType.COMPLEX_POLYMER_COMPONENT:
      return columnDefinition.name ?? 'Complex polymer component';
    case TableColumnType.COMPLEX_POLYMER_CONNECTIONS:
      return columnDefinition.name ?? 'Complex polymer connections';
    case TableColumnType.OLIGO_DUPLEX_OFFSET:
      return columnDefinition.name ?? 'Forward offset';
    case TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN:
      return columnDefinition.name ?? 'Confirmed in';
    case TableColumnType.PROCEDURE_PARAMETER_MIN_MAX:
      return columnDefinition.name ?? 'Min - Max';
    case TableColumnType.ANTIBODY_DOMAIN: {
      // Antibody domain columns should always have an explicit column name.
      return columnDefinition.name ?? '';
    }
    default:
      return '';
  }
}

function renderColumnLabelWithComputedIcon(label: string): JSX.Element {
  return (
    <span>
      <span>{label}</span>
      <FormulaIcon className="u-mini-spacing-left mediocre-tableEditable-axisCell--fontWeightRegular" />
    </span>
  );
}

function renderColumnLabelWithConvertedFromIcon(label: string): JSX.Element {
  return (
    <span>
      <span>{label}</span>
      <BenchlingIcon
        className="u-mini-spacing-left mediocre-tableEditable-axisCell--fontWeightRegular"
        icon="converted-from"
      />
    </span>
  );
}

/**
 * Return a node to be rendered in the column header.
 */
export function renderColumnLabel(columnDefinition: ColumnDefinition): JSX.Element | string {
  if (columnDefinition.type === TableColumnType.TAG_VALUE && columnDefinition.tagSchemaFieldIsComputed) {
    return renderColumnLabelWithComputedIcon(columnDefinition.tagSchemaFieldDisplayName);
  }
  if (columnDefinition.type === TableColumnType.MEASUREMENT_AMOUNT && columnDefinition.isComputed) {
    return renderColumnLabelWithComputedIcon(getColumnLabel(columnDefinition));
  }
  if (
    columnDefinition.type === TableColumnType.TAG_VALUE &&
    columnDefinition.tagSchemaFieldIsConvertedFromLink
  ) {
    return renderColumnLabelWithConvertedFromIcon(columnDefinition.tagSchemaFieldDisplayName);
  }

  return getColumnLabel(columnDefinition);
}

export interface ColumnIdentifier {
  type: TableColumnType;
  columnGroup?: ColumnGroup;
  tagSchemaFieldApiId?: string | null;
}

/**
 * Get the column index for a particular `columnDefinitionType` and
 * `columnGroup`.  If `isOptional` is not specified or is false, this
 * method will throw if there is no column that matches the
 * `columnDefinitionType` and `columnGroup`.  If `isOptional` is true,
 * then this method will return -1 if there is no column that matches
 * the criteria.
 *
 * @param columnGroup: the columnGroup, which is valid for TransferTables
 * @param columnDefinitionType: the COLUMN_DEFINITION which we are looking for
 * @param tableColumnDefinitions: the actual ColumnDefinitions for the table
 * @param isOptional: true or false, if unspecified it will be false
 */
export function getColumnDefinitionIndex({
  columnGroup,
  columnDefinitionType,
  tableColumnDefinitions,
  isOptional,
}: {
  columnGroup?: ColumnGroup;
  columnDefinitionType: string;
  tableColumnDefinitions: Array<ColumnIdentifier | null | undefined>;
  isOptional?: boolean;
}): number {
  const columnIndicesMatchingType = [];
  for (const [i, columnDefinition] of tableColumnDefinitions.entries()) {
    if (
      columnDefinition == null ||
      (columnGroup && columnDefinition.columnGroup !== columnGroup) ||
      columnDefinition.type !== columnDefinitionType
    ) {
      continue;
    }

    columnIndicesMatchingType.push(i);
  }

  const numMatchingColumns = columnIndicesMatchingType.length;
  assert(numMatchingColumns <= 1, `Expected table to have exactly one ${columnDefinitionType} column`, {
    extra: {numMatchingColumns},
  });
  if (isOptional !== true) {
    assert(numMatchingColumns > 0, `Expected table to have a ${columnDefinitionType} column`);
  }
  return columnIndicesMatchingType.length === 0 ? -1 : columnIndicesMatchingType[0];
}

export function isColumnDefinition(
  columnOrColumnDef: StructuredTableColumn | ColumnDefinition
): columnOrColumnDef is ColumnDefinition {
  return columnOrColumnDef.hasOwnProperty('type');
}

export function getColumnTypeFromColumnOrDefinition(
  columnOrColumnDefinition: StructuredTableColumn | ColumnDefinition
): TableColumnType {
  return isColumnDefinition(columnOrColumnDefinition)
    ? columnOrColumnDefinition.type
    : columnOrColumnDefinition.columnType;
}

/**
 * Get the column index for the sample column in a table. Returns -1 if not found, or the col index.
 */
export function getColumnDefinitionIndexForSample(
  columnOrColumnDefinitions: Array<StructuredTableColumn | ColumnDefinition | null | undefined>,
  columnDefinitionType: TableColumnType.BATCH | TableColumnType.EXISTING_BIOENTITY
): number {
  return columnOrColumnDefinitions.findIndex(
    (columnOrColumnDef) =>
      columnOrColumnDef != null &&
      getColumnTypeFromColumnOrDefinition(columnOrColumnDef) === columnDefinitionType &&
      // The primary sample column should never have a column group
      !columnOrColumnDef.columnGroup
  );
}

/**
 * Return the table cell corresponding to the given column type and row.
 *
 * Expects the table row to have exactly 1 cell matching the column type.
 *
 * @param {*} columnGroup If provided, only search for columns within the column group.
 * @param {*} columnDefinitionType Column type to search for.
 * @param {*} tableColumnDefinitions Ordered list of the table's column definitions.
 * @param {*} tableRow Array of table cells.
 * @returns The table cell with the given column type.
 */
export function getCellOfColumnType({
  columnGroup,
  columnDefinitionType,
  tableColumnDefinitions,
  tableRow,
}: {
  columnGroup?: ColumnGroup;
  columnDefinitionType: TableColumnType;
  tableColumnDefinitions: Array<ColumnDefinition>;
  tableRow: Array<Cell>;
}): Cell {
  const i = getColumnDefinitionIndex({columnGroup, columnDefinitionType, tableColumnDefinitions});
  return tableRow[i];
}

/**
 * Returns all table cells corresponding to the given column type.
 *
 * Expects the table to have exactly 1 column matching the given type and group.
 */
export function getAllCellsOfColumnType({
  columnGroup,
  columnDefinitionType,
  tableColumnDefinitions,
  table,
}: {
  columnGroup?: ColumnGroup;
  columnDefinitionType: TableColumnType;
  tableColumnDefinitions: Array<ColumnDefinition>;
  table: Array<Array<Cell>>;
}): Array<Cell> {
  const columnIndex = getColumnDefinitionIndex({
    columnGroup,
    columnDefinitionType,
    tableColumnDefinitions,
    isOptional: true,
  });

  return columnIndex === -1 ? [] : table.map((row) => row[columnIndex]);
}

export function getOptionalCellOfColumnType({
  columnDefinitionType,
  tableColumnDefinitions,
  tableRow,
}: {
  columnDefinitionType: TableColumnType;
  tableColumnDefinitions: Array<ColumnDefinition>;
  tableRow: Array<Cell>;
}): Cell | null {
  const i = getColumnDefinitionIndex({columnDefinitionType, tableColumnDefinitions, isOptional: true});
  if (i === -1) {
    return null;
  }
  return tableRow[i];
}

/**
 * Return the table cell corresponding to the given column type and row.
 *
 * Expects the table row to have exactly 1 cell matching the column type.
 *
 * @param {*} columnGroup If provided, only search for columns within the column group.
 * @param {*} columnDefinitionType Column type to search for.
 * @param {*} tableColumnDefinitions Ordered list of the table's column definitions.
 * @param {*} evaluatedRow Array of evaluated table cells.
 * @returns The table cell with the given column type.
 */
export function getEvaluatedCellOfColumnType({
  columnGroup,
  columnDefinitionType,
  tableColumnDefinitions,
  evaluatedRow,
}: {
  columnGroup?: ColumnGroup;
  columnDefinitionType: TableColumnType;
  tableColumnDefinitions: Array<ColumnDefinition>;
  evaluatedRow: Array<EvaluatedCell>;
}): EvaluatedCell {
  const i = getColumnDefinitionIndex({columnGroup, columnDefinitionType, tableColumnDefinitions});
  return evaluatedRow[i];
}

export function getOptionalEvaluatedCellOfColumnType({
  columnDefinitionType,
  tableColumnDefinitions,
  evaluatedRow,
  columnGroup,
}: {
  columnDefinitionType: TableColumnType;
  tableColumnDefinitions: Array<ColumnDefinition>;
  evaluatedRow: Array<EvaluatedCell>;
  columnGroup?: ColumnGroup;
}): EvaluatedCell | null {
  const i = getColumnDefinitionIndex({
    columnGroup,
    columnDefinitionType,
    tableColumnDefinitions,
    isOptional: true,
  });
  if (i === -1) {
    return null;
  }
  return evaluatedRow[i];
}

/**
 * Return the table row corresponding to immutably updating the cell at the given column type and row.
 *
 * Expects the table row to have exactly 1 cell matching the column type.
 *
 * @param {*} columnGroup If provided, only search for columns within the column group.
 * @param {*} columnDefinitionType Column type to search for.
 * @param {*} tableColumnDefinitions Ordered list of the table's column definitions.
 * @param {*} tableRow Array of table cells.
 * @param {*} cellValue The new cell value.
 * @returns The table row with the updated cell.
 */
export function updateCellOfColumnType({
  columnGroup,
  columnDefinitionType,
  tableColumnDefinitions,
  tableRow,
  cellValue,
}: {
  columnGroup?: ColumnGroup;
  columnDefinitionType: TableColumnType;
  tableColumnDefinitions: Array<ColumnIdentifier | null | undefined>;
  tableRow: Array<Cell>;
  cellValue: Cell;
}): {newRow: Array<Cell>; index: number} {
  const index = getColumnDefinitionIndex({columnGroup, columnDefinitionType, tableColumnDefinitions});
  return {
    newRow: produce(tableRow, (draftRow) => {
      draftRow[index] = cellValue;
    }),
    index,
  };
}

export function isBooleanValueColumnDefinition(columnDefinition: ColumnDefinition): boolean {
  return [TableColumnType.BOOLEAN, TableColumnType.REQUEST_TASK_SELECTION].includes(columnDefinition.type);
}

export function schemaFieldToColumnDefinition(
  schemaField: SchemaFieldConstants.SchemaField
): SchemaFieldColumnDefinition {
  return {
    type: TableColumnType.TAG_VALUE,
    isRequired: schemaField.isRequired,
    tagSchemaFieldType: schemaField.fieldType,
    tagSchemaFieldApiId: schemaField.apiId,
    tagSchemaFieldDisplayName: schemaField.displayName,
    tagSchemaFieldIsComputed: schemaField.isComputed,
    tagSchemaFieldIsConvertedFromLink: schemaField.isConvertedFromLink,
    tagSchemaFieldIsSnapshot: schemaField.isSnapshot,
    tagSchemaFieldDescription: schemaField.tooltipText,
    tagSchemaFieldDecimalPrecision: schemaField.decimalPrecision,
    unitApiIdentifier: schemaField.unitApiIdentifier ?? null,
    schemaField,
  };
}

export function isColumnOutdated(columnDefinition: ColumnDefinition | null): boolean {
  if (!columnDefinition) {
    return false;
  }
  if (!isWithSchemaFieldColumnDefinition(columnDefinition)) {
    return false;
  }
  const {
    schemaField,
    isRequired,
    tagSchemaFieldType,
    tagSchemaFieldApiId,
    tagSchemaFieldDisplayName,
    tagSchemaFieldIsComputed,
    tagSchemaFieldIsConvertedFromLink,
  } = columnDefinition;

  if (columnDefinition.type !== TableColumnType.MISSING_FIELD && isSchemaFieldArchived(schemaField)) {
    // The schemaField became archived
    return true;
  }

  if (columnDefinition.type === TableColumnType.MISSING_FIELD && !isSchemaFieldArchived(schemaField)) {
    // The schemaField use to be archived and it is unarchived now
    return true;
  }

  return (
    isRequired !== schemaField.isRequired ||
    tagSchemaFieldType !== schemaField.fieldType ||
    tagSchemaFieldApiId !== schemaField.apiId ||
    tagSchemaFieldDisplayName !== schemaField.displayName ||
    tagSchemaFieldIsComputed !== schemaField.isComputed ||
    tagSchemaFieldIsConvertedFromLink !== schemaField.isConvertedFromLink ||
    (columnDefinition.type === TableColumnType.TAG_VALUE &&
      (columnDefinition.tagSchemaFieldDescription !== schemaField.tooltipText ||
        columnDefinition.unitApiIdentifier !== (schemaField.unitApiIdentifier ?? null) ||
        columnDefinition.tagSchemaFieldDecimalPrecision !== schemaField.decimalPrecision))
  );
}
